import { NavLink, useSearchParams } from 'react-router-dom';
import {
  LayoutDashboard, BookOpen, FileUp, FileText, Trophy,
  Clapperboard, MapPin, UserCheck, CalendarDays, Landmark,
  PanelLeftClose, PanelLeftOpen, Palette, FolderOpen,
  CheckCircle2, Circle, Wallet, Target, ScrollText, Map, Sparkles,
  Activity, Database, Wand2, Film, CreditCard, UserCog, Mic, Users, Lock, Leaf, CalendarRange,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { useStageSignals } from '@/hooks/useScriptAnalysis';
import { useAuth } from '@/hooks/useAuth';
import { useOrganization, isPlanActive } from '@/hooks/useOrganization';
import { useMyMembership } from '@/hooks/useMyMembership';
import { useCanManageTeam } from '@/hooks/useCanManageTeam';
import { resolvePlan, planAllows } from '@/lib/plans';
import { tabAllowed, ALL_TAB_KEYS } from '@/lib/team-tabs';
import { veLaHuellaDeCarbono } from '@/lib/beta-huella';
import ThemeToggle from '@/components/common/ThemeToggle';
import { STAGE_CONFIG, esStageDePipeline } from '@/types/scriptAnalysis';
import type { StageName } from '@/types/scriptAnalysis';
import { deriveStageProgress, emptyStageSignals, type StageProgress, type StageSignals } from '@/lib/stage-progress';

const STAGE_ROUTES: Record<StageName, string> = {
  narrativa: '/narrativa',
  moodboard: '/moodboard',
  localizaciones: '/produccion/localizaciones',
  rodaje: '/produccion/rodaje',
  casting: '/produccion/casting',
  diseno_produccion: '/produccion/desglose',
  postproduccion: '/produccion/vfx',
  presupuesto: '/presupuesto/icaa',
  eco: '/huella-carbono',
  financiacion: '/produccion/financiacion',
  audiencias: '/audiencias',
  festivales: '/distribucion/festivales',
};

const STAGE_ICONS: Record<StageName, React.ReactNode> = {
  narrativa: <BookOpen size={16} />,
  moodboard: <Palette size={16} />,
  localizaciones: <MapPin size={16} />,
  rodaje: <CalendarDays size={16} />,
  casting: <UserCheck size={16} />,
  diseno_produccion: <ScrollText size={16} />,
  postproduccion: <Wand2 size={16} />,
  presupuesto: <Wallet size={16} />,
  eco: <Leaf size={16} />,
  financiacion: <Landmark size={16} />,
  audiencias: <Target size={16} />,
  festivales: <Trophy size={16} />,
};

// Item que sigue inmediatamente al bloque ETAPAS — cuelga de "Audiencias" como
// el output natural del proceso (qué festivales atacar con la estrategia diseñada).
const POST_STAGE_ITEM = {
  label: 'Plan Festivales',
  path: '/distribucion/plan-festivales',
  icon: <Trophy size={16} />,
};

// Huella de Carbono: pestaña propia, colgada justo detrás de Presupuesto (misma llave de
// plan que esa etapa: no toca las 4 features de PRODUCTION_FEATURES). Su chat SÍ es una
// etapa propia (stage 'eco', el Eco Manager), pero esa etapa se queda fuera del pipeline
// de la nav (ver STAGES_FUERA_DEL_PIPELINE): la pinta este item, y si además saliera como
// etapa aparecería dos veces en el menú. Igual que Breakdown VFX con 'postproduccion'.
const CARBONO_ITEM = {
  label: 'Huella de Carbono',
  path: '/huella-carbono',
  icon: <Leaf size={16} />,
};

// Antes un único bloque "Datos" (cajón de sastre). Partido en 3 secciones claras:
// Proyecto (vistas del guion), Documentos (entregables) y Cuenta (abajo, discreto).
// `feature` (opcional) = clave de gating por plan (ver src/lib/plans.ts).
type NavItem = { label: string; path: string; icon: React.ReactNode; feature?: string; locked?: boolean; staffOnly?: boolean };
const PROYECTO_ITEMS: NavItem[] = [
  { label: 'Planos', path: '/planos', icon: <Film size={16} /> },
  { label: 'Escenas', path: '/produccion/escenas', icon: <Clapperboard size={16} /> },
  { label: 'Mapa', path: '/produccion/mapa', icon: <Map size={16} />, feature: 'mapa' },
  { label: 'Breakdown VFX', path: '/produccion/vfx', icon: <Wand2 size={16} />, feature: 'vfx' },
  { label: 'Calendario de preproducción', path: '/produccion/preproduccion', icon: <CalendarRange size={16} />, staffOnly: true },
  // Desglose = "Diseño de Producción" en ETAPAS. Localizaciones también es STAGE.
];
const DOCUMENTOS_ITEMS: NavItem[] = [
  // Kit Guionista oculto del menú (no se usa ni se saca a la venta). Código y ruta
  // /kit-guionista se mantienen (reversible): solo se retira del nav.
  { label: 'Memorias de intenciones', path: '/proyecto/documentos', icon: <FileText size={16} /> },
  { label: 'Equipo y Reparto', path: '/produccion/equipo', icon: <Users size={16} /> },
  { label: 'Dossier Builder', path: '/dossier-builder', icon: <Sparkles size={16} /> },
];
const CUENTA_ITEMS: NavItem[] = [
  { label: 'Perfil del productor', path: '/perfil-productor', icon: <UserCog size={16} /> },
  { label: 'Mi voz', path: '/perfil-productor?tab=voz', icon: <Mic size={16} /> },
  { label: 'Suscripción', path: '/plan', icon: <CreditCard size={16} /> },
];
// Combinado (solo para el modo colapsado: iconos sin cabeceras).
const DATOS_ITEMS = [...PROYECTO_ITEMS, ...DOCUMENTOS_ITEMS, ...CUENTA_ITEMS];

const GUIONISTA_ITEMS = [
  { label: 'Narrativa', path: '/narrativa', icon: <BookOpen size={16} /> },
  { label: 'Mi voz', path: '/perfil-productor', icon: <UserCog size={16} /> },
];

// Estado AUTOMÁTICO derivado de los datos del proyecto (mismo motor que el
// Dashboard). Sin candados: una etapa sin datos se ve "sin empezar" (círculo
// tenue), no bloqueada.
function StageStatusIcon({ status }: { status: StageProgress }) {
  switch (status) {
    case 'has_data':
      return <CheckCircle2 size={14} className="text-emerald-700 dark:text-emerald-400 shrink-0" />;
    case 'in_progress':
      return <Circle size={14} className="text-pelip-accent fill-pelip-accent shrink-0" />;
    case 'empty':
    default:
      return <Circle size={14} className="text-pelip-text-faint shrink-0" />;
  }
}

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('job');
  const jobParam = jobId ? `?job=${jobId}` : '';
  // Une el jobParam respetando queries ya presentes en el path (p.ej. ?tab=voz).
  const withJob = (path: string) => (path.includes('?') ? `${path}${jobParam.replace('?', '&')}` : `${path}${jobParam}`);
  const { data: stageSignals } = useStageSignals(jobId);
  const sig: StageSignals = stageSignals ?? emptyStageSignals();
  const { isOwner, isStaff, user } = useAuth();
  // Manda el plan de la organización DUEÑA del proyecto abierto (mismo criterio que el
  // backend): dentro de un proyecto de la productora que te invitó, ves sus funciones.
  const { data: org } = useOrganization(jobId);
  const guionistaMode = isPlanActive(org, ['guionista']);
  // Gating por plan: qué features ve este org en el sidebar (ver src/lib/plans.ts).
  // creative (Indie) capa vfx/mapa/rodaje/localizaciones; all (One Shot/Productora/Estudio) no capa nada.
  const featureSet = resolvePlan(org?.plan).featureSet;
  const allowFeature = (feature?: string) => !feature || planAllows(featureSet, feature);
  // Filtro por permisos de miembro: si el usuario es un member con allowed_tabs no-null,
  // solo ve las pestañas permitidas (además del gating por plan). El owner ve todo. La
  // clave de cada pestaña es su StageName (etapas) o su path (resto), ver src/lib/team-tabs.ts.
  const { data: membership } = useMyMembership();
  const { data: canManageTeam } = useCanManageTeam();
  const restrictByTabs = membership?.role === 'member' && membership.allowed_tabs != null;
  // Una pestaña se muestra si (a) no aplica el filtro de miembro, (b) NO es una pestaña
  // limitable (p.ej. las de Cuenta: Perfil, Mi voz, Suscripción — nunca se limitan) o
  // (c) su clave está en allowed_tabs. La clave = StageName (etapas) o path (resto).
  const showTab = (key: string) =>
    !restrictByTabs || !ALL_TAB_KEYS.includes(key) || tabAllowed(membership?.allowed_tabs, key);
  // Upsell: en lugar de ocultar los items capados, los marcamos como locked para que
  // el usuario los vea bloqueados y suba de plan. El clic navega normalmente a la ruta
  // y el FeatureGate de esa ruta muestra el aviso "Disponible desde Productora".
  // Los items que el miembro no tiene permitidos se ocultan (filter), no se bloquean.
  const proyectoItems = PROYECTO_ITEMS
    .filter((i) => showTab(i.path) && (!i.staffOnly || isStaff))
    .map((i) => ({ ...i, locked: !allowFeature(i.feature) }));
  const documentosItems = DOCUMENTOS_ITEMS.filter((i) => showTab(i.path));
  // "Equipo" (gestión de miembros): owner de una organización Productora/Estudio (o staff
  // de Pelip). Va en la sección Cuenta. OJO: `isOwner` es el rol de plataforma, no el de
  // dueño de la organización, y por sí solo dejaba el enlace oculto a todos los clientes.
  const cuentaItems: NavItem[] = isOwner || canManageTeam
    ? [...CUENTA_ITEMS, { label: 'Equipo', path: '/equipo', icon: <Users size={16} /> }]
    : CUENTA_ITEMS;
  const datosItems = [
    ...DATOS_ITEMS,
    ...(isOwner || canManageTeam
      ? [{ label: 'Equipo', path: '/equipo', icon: <Users size={16} /> } as NavItem]
      : []),
  ].filter((i) => showTab(i.path) && (!i.staffOnly || isStaff))
   .map((i) => ({ ...i, locked: !allowFeature(i.feature) }));
  // 'festivales', 'postproduccion' y 'eco' son etapas de CHAT pero NO van en el pipeline
  // de la nav: ya tienen su propio ítem ("Plan Festivales" bajo Audiencias; "Breakdown
  // VFX" bajo Proyecto, gateado feature 'vfx'; "Huella de Carbono" tras Presupuesto).
  // Solo se excluyen de la navegación de etapas; sus chats se montan vía StageLayout
  // (stage="festivales"/"postproduccion"/"eco").
  // localizaciones y rodaje se muestran bloqueadas en plan creative (upsell).
  const visibleStages = STAGE_CONFIG
    .filter((s) => esStageDePipeline(s.name))
    .filter((s) => showTab(s.name))
    .map((s) => ({ ...s, locked: !allowFeature(s.name) }));
  // "Plan Festivales" (post-etapa) también es una pestaña limitable (por su path).
  const showPostStage = showTab(POST_STAGE_ITEM.path);
  // Huella de Carbono: misma llave de plan que Presupuesto (nunca capada en creative/all).
  // Durante la beta cerrada solo la ven las cuentas de casa (ver src/lib/beta-huella.ts).
  const showCarbono = showTab(CARBONO_ITEM.path) && veLaHuellaDeCarbono(user?.id);
  const carbonoLocked = !allowFeature('presupuesto');

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={`bg-pelip-bg-elev border-r border-pelip-border flex flex-col h-screen fixed left-0 top-0 z-40 transition-all duration-200 ${
          collapsed ? 'w-16' : 'w-60'
        }`}
      >
        {/* Logo + Collapse Toggle */}
        <div className="px-3 py-4 border-b border-pelip-border flex items-center justify-between">
          {collapsed ? (
            <NavLink to="/proyectos" className="mx-auto group">
              <img src="/brand/pelip-symbol-accent.svg" alt="pelip" className="w-7 h-7" />
            </NavLink>
          ) : (
            <NavLink to="/proyectos" className="group flex items-center gap-2">
              <img src="/brand/pelip-h-mono-white.svg" alt="pelip" className="h-5 w-auto invert dark:invert-0" />
              <span className="font-mono text-[9px] text-pelip-text-faint uppercase tracking-wider">AI</span>
            </NavLink>
          )}
          <button
            onClick={onToggle}
            className={`text-pelip-text-faint hover:text-pelip-text-soft transition-colors ${collapsed ? 'hidden' : ''}`}
          >
            <PanelLeftClose size={16} />
          </button>
        </div>

        {/* CTA primario · Subir Guion — arriba y SIEMPRE visible (antes estaba al
            fondo del sidebar, el peor sitio para el punto de entrada nº1). Bloque fijo
            fuera del <nav> con scroll, así no se pierde de vista. */}
        <div className={`${collapsed ? 'px-1' : 'px-2'} pt-3`}>
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <NavLink
                  to="/subir"
                  className="flex items-center justify-center w-10 h-10 mx-auto bg-pelip-primary hover:bg-pelip-primary-hover text-white rounded-lg transition-colors cursor-pointer"
                >
                  <FileUp size={16} />
                </NavLink>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8}>Subir Guion</TooltipContent>
            </Tooltip>
          ) : (
            <NavLink
              to="/subir"
              className="flex items-center justify-center gap-2 w-full px-3 py-2.5 bg-pelip-primary hover:bg-pelip-primary-hover text-white text-sm font-medium rounded-lg transition-colors cursor-pointer"
            >
              <FileUp size={16} />
              Subir Guion
            </NavLink>
          )}
        </div>

        {/* Navigation */}
        <nav className={`flex-1 overflow-y-auto py-3 ${collapsed ? 'px-1' : 'px-2'} space-y-1`}>
          {/* Proyectos */}
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <NavLink
                  to="/proyectos"
                  className="flex items-center justify-center w-10 h-10 mx-auto rounded-lg transition-colors text-pelip-text-muted hover:text-pelip-text-soft hover:bg-pelip-bg-elev-2"
                >
                  <FolderOpen size={18} />
                </NavLink>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8}>Proyectos</TooltipContent>
            </Tooltip>
          ) : (
            <NavLink
              to="/proyectos"
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors cursor-pointer text-pelip-text-muted hover:text-pelip-text-soft hover:bg-pelip-bg-elev-2"
            >
              <FolderOpen size={18} />
              Proyectos
            </NavLink>
          )}

          {/* Dashboard */}
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <NavLink
                  to={`/${jobParam}`}
                  end
                  className={({ isActive }) =>
                    `flex items-center justify-center w-10 h-10 mx-auto rounded-lg transition-colors ${
                      isActive
                        ? 'bg-pelip-primary/15 text-pelip-accent'
                        : 'text-pelip-text-soft hover:text-pelip-text hover:bg-pelip-bg-elev-2'
                    }`
                  }
                >
                  <LayoutDashboard size={18} />
                </NavLink>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8}>Dashboard</TooltipContent>
            </Tooltip>
          ) : (
            <NavLink
              to={`/${jobParam}`}
              end
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors cursor-pointer ${
                  isActive
                    ? 'bg-pelip-primary/15 text-pelip-accent font-medium'
                    : 'text-pelip-text-soft hover:text-pelip-text hover:bg-pelip-bg-elev-2'
                }`
              }
            >
              <LayoutDashboard size={18} />
              Dashboard
            </NavLink>
          )}

          <div className="h-2" />

          {/* Guionista plan: focused nav (Guionista, Narrativa) */}
          {guionistaMode && (
            <>
              {collapsed ? (
                <div className="flex flex-col items-center gap-0.5">
                  {GUIONISTA_ITEMS.map((item) => (
                    <Tooltip key={item.path}>
                      <TooltipTrigger asChild>
                        <NavLink
                          to={withJob(item.path)}
                          className={({ isActive }) =>
                            `flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
                              isActive
                                ? 'bg-pelip-primary/15 text-pelip-accent'
                                : 'text-pelip-text-muted hover:text-pelip-text-soft hover:bg-pelip-bg-elev-2'
                            }`
                          }
                        >
                          {item.icon}
                        </NavLink>
                      </TooltipTrigger>
                      <TooltipContent side="right" sideOffset={8}>{item.label}</TooltipContent>
                    </Tooltip>
                  ))}
                </div>
              ) : (
                <div>
                  <div className="px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-pelip-text-faint">
                    Guionista
                  </div>
                  <div className="space-y-0.5">
                    {GUIONISTA_ITEMS.map((item) => (
                      <NavLink
                        key={item.path}
                        to={`${item.path}${jobParam}`}
                        className={({ isActive }) =>
                          `flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
                            isActive
                              ? 'bg-pelip-primary/15 text-pelip-accent font-medium'
                              : 'text-pelip-text-soft hover:text-pelip-text hover:bg-pelip-bg-elev-2'
                          }`
                        }
                      >
                        {item.icon}
                        {item.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Studio / agency / free: full nav (ETAPAS + POST_STAGE + DATOS) */}
          {!guionistaMode && (
            <>
              {/* ETAPAS section */}
              {collapsed ? (
                <div className="flex flex-col items-center gap-0.5">
                  {visibleStages.map((stage) => {
                    const status = deriveStageProgress(stage.name, sig);
                    return (
                      <div key={stage.name} className="contents">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <NavLink
                              to={`${STAGE_ROUTES[stage.name]}${jobParam}`}
                              className={({ isActive }) =>
                                `flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
                                  stage.locked
                                    ? 'opacity-60 text-pelip-text-faint'
                                    : isActive
                                      ? 'bg-pelip-primary/15 text-pelip-accent'
                                      : 'text-pelip-text-muted hover:text-pelip-text-soft hover:bg-pelip-bg-elev-2'
                                }`
                              }
                            >
                              {STAGE_ICONS[stage.name]}
                            </NavLink>
                          </TooltipTrigger>
                          <TooltipContent side="right" sideOffset={8}>
                            {stage.locked ? (
                              <div className="flex items-center gap-1.5">
                                <Lock size={10} className="shrink-0" />
                                {stage.label} · Disponible en Productora
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <StageStatusIcon status={status} />
                                {stage.label}
                              </div>
                            )}
                          </TooltipContent>
                        </Tooltip>
                        {/* Huella de Carbono · pestaña propia, junto a Presupuesto */}
                        {stage.name === 'presupuesto' && showCarbono && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <NavLink
                                to={`${CARBONO_ITEM.path}${jobParam}`}
                                className={({ isActive }) =>
                                  `flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
                                    carbonoLocked
                                      ? 'opacity-60 text-pelip-text-faint'
                                      : isActive
                                        ? 'bg-pelip-primary/15 text-pelip-accent'
                                        : 'text-pelip-text-muted hover:text-pelip-text-soft hover:bg-pelip-bg-elev-2'
                                  }`
                                }
                              >
                                {CARBONO_ITEM.icon}
                              </NavLink>
                            </TooltipTrigger>
                            <TooltipContent side="right" sideOffset={8}>
                              {carbonoLocked ? (
                                <div className="flex items-center gap-1.5">
                                  <Lock size={10} className="shrink-0" />
                                  {CARBONO_ITEM.label} · Disponible en Productora
                                </div>
                              ) : CARBONO_ITEM.label}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    );
                  })}
                  {/* Plan Festivales · cuelga directamente de Audiencias */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <NavLink
                        to={`${POST_STAGE_ITEM.path}${jobParam}`}
                        className={({ isActive }) =>
                          `flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
                            isActive
                              ? 'bg-pelip-primary/15 text-pelip-accent'
                              : 'text-pelip-text-muted hover:text-pelip-text-soft hover:bg-pelip-bg-elev-2'
                          }`
                        }
                      >
                        {POST_STAGE_ITEM.icon}
                      </NavLink>
                    </TooltipTrigger>
                    <TooltipContent side="right" sideOffset={8}>{POST_STAGE_ITEM.label}</TooltipContent>
                  </Tooltip>
                </div>
              ) : (
                <div>
                  <div className="px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-pelip-text-faint">
                    Etapas
                  </div>
                  <div className="space-y-0.5">
                    {visibleStages.map((stage) => {
                      const status = deriveStageProgress(stage.name, sig);
                      return (
                        <div key={stage.name} className="space-y-0.5">
                          <NavLink
                            to={`${STAGE_ROUTES[stage.name]}${jobParam}`}
                            title={stage.locked ? 'Disponible en Productora' : undefined}
                            className={({ isActive }) =>
                              `flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
                                stage.locked
                                  ? 'opacity-60 text-pelip-text-faint'
                                  : isActive
                                    ? 'bg-pelip-primary/15 text-pelip-accent font-medium'
                                    : 'text-pelip-text-soft hover:text-pelip-text hover:bg-pelip-bg-elev-2'
                              }`
                            }
                          >
                            {stage.locked ? null : <StageStatusIcon status={status} />}
                            {STAGE_ICONS[stage.name]}
                            {stage.label}
                            {stage.locked && <Lock size={12} className="ml-auto shrink-0" />}
                          </NavLink>
                          {/* Huella de Carbono · pestaña propia, junto a Presupuesto */}
                          {stage.name === 'presupuesto' && showCarbono && (
                            <NavLink
                              to={`${CARBONO_ITEM.path}${jobParam}`}
                              title={carbonoLocked ? 'Disponible en Productora' : undefined}
                              className={({ isActive }) =>
                                `flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
                                  carbonoLocked
                                    ? 'opacity-60 text-pelip-text-faint'
                                    : isActive
                                      ? 'bg-pelip-primary/15 text-pelip-accent font-medium'
                                      : 'text-pelip-text-soft hover:text-pelip-text hover:bg-pelip-bg-elev-2'
                                }`
                              }
                            >
                              {CARBONO_ITEM.icon}
                              {CARBONO_ITEM.label}
                              {carbonoLocked && <Lock size={12} className="ml-auto shrink-0" />}
                            </NavLink>
                          )}
                        </div>
                      );
                    })}
                    {/* Plan Festivales · mismo diseño que el resto de etapas. */}
                    <NavLink
                      to={`${POST_STAGE_ITEM.path}${jobParam}`}
                      className={({ isActive }) =>
                        `flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
                          isActive
                            ? 'bg-pelip-primary/15 text-pelip-accent font-medium'
                            : 'text-pelip-text-soft hover:text-pelip-text hover:bg-pelip-bg-elev-2'
                        }`
                      }
                    >
                      <StageStatusIcon status={deriveStageProgress('festivales', sig)} />
                      {POST_STAGE_ITEM.icon}
                      {POST_STAGE_ITEM.label}
                    </NavLink>
                  </div>
                </div>
              )}

              <div className="h-3" />

              {/* DATOS section */}
              {collapsed ? (
                <div className="flex flex-col items-center gap-0.5">
                  {datosItems.map((item) => (
                    <Tooltip key={item.path}>
                      <TooltipTrigger asChild>
                        <NavLink
                          to={withJob(item.path)}
                          className={({ isActive }) =>
                            `flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
                              item.locked
                                ? 'opacity-60 text-pelip-text-faint'
                                : isActive
                                  ? 'bg-pelip-primary/15 text-pelip-accent'
                                  : 'text-pelip-text-muted hover:text-pelip-text-soft hover:bg-pelip-bg-elev-2'
                            }`
                          }
                        >
                          {item.icon}
                        </NavLink>
                      </TooltipTrigger>
                      <TooltipContent side="right" sideOffset={8}>
                        {item.locked ? (
                          <div className="flex items-center gap-1.5">
                            <Lock size={10} className="shrink-0" />
                            {item.label} · Disponible en Productora
                          </div>
                        ) : item.label}
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {[
                    { label: 'Proyecto', items: proyectoItems },
                    { label: 'Documentos', items: documentosItems },
                    { label: 'Cuenta', items: cuentaItems },
                  ].map((section) => (
                    <div key={section.label}>
                      <div className="px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-pelip-text-faint">
                        {section.label}
                      </div>
                      <div className="space-y-0.5">
                        {section.items.map((item) => (
                          <NavLink
                            key={item.path}
                            to={withJob(item.path)}
                            title={item.locked ? 'Disponible en Productora' : undefined}
                            className={({ isActive }) =>
                              `flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
                                item.locked
                                  ? 'opacity-60 text-pelip-text-faint'
                                  : isActive
                                    ? 'bg-pelip-primary/15 text-pelip-accent font-medium'
                                    : 'text-pelip-text-soft hover:text-pelip-text hover:bg-pelip-bg-elev-2'
                              }`
                            }
                          >
                            {item.icon}
                            {item.label}
                            {item.locked && <Lock size={12} className="ml-auto shrink-0" />}
                          </NavLink>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </nav>

        {/* Bottom: Collapse expand button (collapsed state) + Upload */}
        <div className="p-2 border-t border-pelip-border space-y-2">
          <ThemeToggle collapsed={collapsed} />
          {collapsed && (
            <button
              onClick={onToggle}
              className="flex items-center justify-center w-full h-9 text-pelip-text-faint hover:text-pelip-text-soft transition-colors rounded-lg hover:bg-pelip-bg-elev-2"
            >
              <PanelLeftOpen size={16} />
            </button>
          )}
          {/* Admin · Eval Telemetry · solo visible para owners */}
          {isOwner && (collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <NavLink
                  to="/admin/eval"
                  className={({ isActive }) =>
                    `flex items-center justify-center w-10 h-10 mx-auto rounded-lg transition-colors ${
                      isActive
                        ? 'bg-emerald-100 dark:bg-emerald-600/20 text-emerald-700 dark:text-emerald-400'
                        : 'text-pelip-text-faint hover:text-emerald-300 hover:bg-pelip-bg-elev-2'
                    }`
                  }
                >
                  <Activity size={16} />
                </NavLink>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8}>Eval Telemetry</TooltipContent>
            </Tooltip>
          ) : (
            <NavLink
              to="/admin/eval"
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
                  isActive
                    ? 'bg-emerald-100 dark:bg-emerald-600/20 text-emerald-800 dark:text-emerald-300 font-medium'
                    : 'text-pelip-text-muted hover:text-emerald-300 hover:bg-pelip-bg-elev-2'
                }`
              }
            >
              <Activity size={16} />
              Eval Telemetry
              <span className="ml-auto font-mono text-[9px] uppercase tracking-wider text-pelip-text-faint">admin</span>
            </NavLink>
          ))}
          {/* Admin · Tarifas de proveedores · solo owners */}
          {isOwner && (collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <NavLink
                  to="/admin/tarifas"
                  className={({ isActive }) =>
                    `flex items-center justify-center w-10 h-10 mx-auto rounded-lg transition-colors ${
                      isActive
                        ? 'bg-emerald-100 dark:bg-emerald-600/20 text-emerald-700 dark:text-emerald-400'
                        : 'text-pelip-text-faint hover:text-emerald-300 hover:bg-pelip-bg-elev-2'
                    }`
                  }
                >
                  <Database size={16} />
                </NavLink>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8}>Tarifas de proveedores</TooltipContent>
            </Tooltip>
          ) : (
            <NavLink
              to="/admin/tarifas"
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
                  isActive
                    ? 'bg-emerald-100 dark:bg-emerald-600/20 text-emerald-800 dark:text-emerald-300 font-medium'
                    : 'text-pelip-text-muted hover:text-emerald-300 hover:bg-pelip-bg-elev-2'
                }`
              }
            >
              <Database size={16} />
              Tarifas proveedores
              <span className="ml-auto font-mono text-[9px] uppercase tracking-wider text-pelip-text-faint">admin</span>
            </NavLink>
          ))}
        </div>
      </aside>
    </TooltipProvider>
  );
}
